// src/Pages/Modules/Massdata/Massdata.jsx
import React, { useState, useRef, useEffect } from 'react';
import './massdata.css';
import { db } from "../../../firebase";         // ✅ FIXED: 4 levels up from Massdata/
import {
    collection,
    getDocs,
    query,
    where,
    writeBatch,
    doc
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import Sidebar from '../../../Component/Sidebar/Sidebar';  // ✅ Added Sidebar
import { useNavigate } from 'react-router-dom';             // ✅ Added navigation

// ─── Constants ───────────────────────────────────────────────────────────────
const OWNERSHIP_MAP = {
    'own vehicle': 'own', 'own tanker': 'own', 'own': 'own',
    'external vehicle': 'external', 'external tanker': 'external', 'external': 'external'
};
const STATUS_VALUES = ['active', 'maintenance'];
const TYPE_VALUES = ['vehicle', 'tanker'];

// ─── Template generator ──────────────────────────────────────────────────────
const downloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    const vehicleData = [
        ['type', 'truckNumber', 'model', 'location', 'currentReading', 'driverName', 'ownership', 'status'],
        ['Vehicle', 'MH12AB1234', 'Tata Ace', 'Mumbai', '12500', 'Ramesh Kumar', 'Own Vehicle', 'Active'],
        ['Vehicle', 'DL01CD5678', 'Ashok Leyland', 'Delhi', '8200', 'Suresh Singh', 'External Vehicle', 'Active'],
    ];
    const tankerData = [
        ['type', 'truckNumber', 'model', 'location', 'capacity', 'remainingOil', 'driverName', 'ownership', 'status'],
        ['Tanker', 'GJ05EF9012', 'HPCL Tanker 6000L', 'Ahmedabad', '6000', '4500', 'Dinesh Patel', 'Own Tanker', 'Active'],
        ['Tanker', 'KA09GH3456', 'BPCL Tanker 8000L', 'Bangalore', '8000', '6000', 'Mahesh Rao', 'External Tanker', 'Maintenance'],
    ];

    const wsV = XLSX.utils.aoa_to_sheet(vehicleData);
    const wsT = XLSX.utils.aoa_to_sheet(tankerData);
    wsV['!cols'] = [{ wch: 10 }, { wch: 16 }, { wch: 20 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 22 }, { wch: 14 }];
    wsT['!cols'] = [{ wch: 10 }, { wch: 16 }, { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 20 }, { wch: 18 }, { wch: 22 }, { wch: 14 }];

    XLSX.utils.book_append_sheet(wb, wsV, 'Vehicles');
    XLSX.utils.book_append_sheet(wb, wsT, 'Tankers');
    XLSX.writeFile(wb, 'Fleet_Upload_Template.xlsx');
};

// ─── Row validator ────────────────────────────────────────────────────────────
const validateRow = (row) => {
    const errors = [];
    const type = String(row.type || '').toLowerCase().trim();

    if (!TYPE_VALUES.includes(type))
        errors.push('Type must be "Vehicle" or "Tanker"');
    if (!row.truckNumber)
        errors.push('Vehicle/Tanker Number is required');
    if (!row.model)
        errors.push('Model is required');
    if (!row.location)
        errors.push('Location is required');
    if (!row.driverName)
        errors.push('Driver Name is required');
    if (!OWNERSHIP_MAP[String(row.ownership || '').toLowerCase().trim()])
        errors.push('Ownership must be "Own Vehicle/Tanker" or "External Vehicle/Tanker"');
    if (type === 'vehicle' && !row.currentReading && row.currentReading !== 0)
        errors.push('Current Reading (km) is required for Vehicle');
    if (type === 'tanker') {
        if (!row.capacity && row.capacity !== 0)
            errors.push('Capacity (L) is required for Tanker');
        if (!row.remainingOil && row.remainingOil !== 0)
            errors.push('Remaining Oil (L) is required for Tanker');
    }
    return errors;
};

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Massdata() {
    const navigate = useNavigate();  // ✅ useNavigate for page-based routing

    const [step, setStep] = useState('upload');
    const [rows, setRows] = useState([]);
    const [fileName, setFileName] = useState('');
    const [dragOver, setDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [results, setResults] = useState({ success: 0, failed: 0, errors: [] });
    const [existingNumbers, setExistingNumbers] = useState(new Set());
    const [filterType, setFilterType] = useState('All');
    const [filterStatus, setFilterStatus] = useState('All');
    const fileInputRef = useRef(null);

    // Fetch existing numbers to detect duplicates
    useEffect(() => {
        const fetchExisting = async () => {
            try {
                const [trucksSnap, tankersSnap] = await Promise.all([
                    getDocs(collection(db, 'trucks')),
                    getDocs(collection(db, 'tankers')),
                ]);
                const nums = new Set([
                    ...trucksSnap.docs.map(d => d.data().truckNumber),
                    ...tankersSnap.docs.map(d => d.data().truckNumber)
                ].filter(Boolean));
                setExistingNumbers(nums);
            } catch (e) {
                console.error('Fetch error:', e);
            }
        };
        fetchExisting();
    }, []);

    // ── Parse Excel ──
    const parseFile = (file) => {
        if (!file) return;
        setFileName(file.name);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'array' });
                const all = [];
                wb.SheetNames.forEach(sheetName => {
                    const raw = XLSX.utils.sheet_to_json(wb.Sheets[sheetName], { defval: '' });
                    raw.forEach((r, i) => {
                        const norm = {};
                        Object.keys(r).forEach(k => { norm[k.trim()] = r[k]; });
                        all.push({
                            _id: `${sheetName}-${i}`,
                            _sheet: sheetName,
                            _errors: validateRow(norm),
                            _dup: existingNumbers.has(String(norm.truckNumber || '').trim()),
                            ...norm
                        });
                    });
                });
                setRows(all);
                setStep('preview');
            } catch (err) {
                console.error('Parse error:', err);
                alert('❌ Failed to parse file. Please use the provided template.');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const handleFileChange = (e) => parseFile(e.target.files[0]);
    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
            parseFile(file);
        } else {
            alert('Please upload a valid .xlsx or .xls file.');
        }
    };

    // ── Derived stats ──
    const validRows = rows.filter(r => r._errors.length === 0 && !r._dup);
    const errorRows = rows.filter(r => r._errors.length > 0);
    const dupRows = rows.filter(r => r._dup);
    const vehicleRows = validRows.filter(r => String(r.type).toLowerCase() === 'vehicle');
    const tankerRows = validRows.filter(r => String(r.type).toLowerCase() === 'tanker');

    const filteredRows = rows.filter(r => {
        const matchType = filterType === 'All' || String(r.type).toLowerCase() === filterType.toLowerCase();
        const matchStatus = filterStatus === 'All' || (r._errors.length === 0 && !r._dup ? 'valid' : 'error') === filterStatus;
        return matchType && matchStatus;
    });

    // ── Firebase Batch Upload ──
    const handleUpload = async () => {
        if (validRows.length === 0) return;
        setStep('uploading');
        setUploadProgress(0);

        let successCount = 0;
        const failedErrors = [];

        for (let i = 0; i < validRows.length; i += 10) {
            const chunk = validRows.slice(i, i + 10);
            try {
                const batch = writeBatch(db);
                chunk.forEach(row => {
                    const type = String(row.type).toLowerCase().trim();
                    const ownership = OWNERSHIP_MAP[String(row.ownership || '').toLowerCase().trim()];
                    const statusRaw = String(row.status || '').toLowerCase().trim();
                    const status = STATUS_VALUES.includes(statusRaw)
                        ? statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1)
                        : 'Active';

                    const collName = type === 'tanker' ? 'tankers' : 'trucks';
                    const docRef = doc(collection(db, collName));

                    const cleanData = {
                        type: row.type.charAt(0).toUpperCase() + row.type.slice(1).toLowerCase(),
                        truckNumber: String(row.truckNumber).trim(),
                        model: String(row.model).trim(),
                        location: String(row.location).trim(),
                        driverName: String(row.driverName).trim(),
                        ownership,
                        status
                    };

                    if (type === 'vehicle') {
                        cleanData.currentReading = Number(row.currentReading) || 0;
                        cleanData.vehicleId = String(row.truckNumber).trim();
                    } else {
                        cleanData.capacity = Number(row.capacity) || 0;
                        cleanData.remainingOil = Number(row.remainingOil) || 0;
                        cleanData.tankerId = String(row.truckNumber).trim();
                    }

                    batch.set(docRef, cleanData);
                    successCount++;
                });
                await batch.commit();
            } catch (err) {
                console.error('Batch error:', err);
                chunk.forEach(r => failedErrors.push({ truckNumber: r.truckNumber, error: 'Write failed' }));
                successCount -= chunk.length;
            }
            setUploadProgress(Math.round(((i + chunk.length) / validRows.length) * 100));
            await new Promise(res => setTimeout(res, 120));
        }

        setResults({ success: successCount, failed: failedErrors.length, errors: failedErrors });
        setStep('done');
    };

    const removeRow = (id) => setRows(prev => prev.filter(r => r._id !== id));
    const resetState = () => { setStep('upload'); setRows([]); setFileName(''); setResults({ success: 0, failed: 0, errors: [] }); };

    // ── Step index for progress bar ──
    const stepIndex = { upload: 0, preview: 1, uploading: 2, done: 2 }[step] ?? 0;

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="massdata-page">
            {/* ✅ Sidebar — same as all other admin pages */}
            <Sidebar />

            <div className="massdata-main">

                {/* ── Page Header ── */}
                <div className="md-page-header">
                    <div className="md-page-header-left">
                        <div className="md-page-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14,2 14,8 20,8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10,9 9,9 8,9" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="md-page-title">Mass Fleet Upload</h1>
                            <p className="md-page-sub">Bulk import Vehicles &amp; Tankers via Excel spreadsheet</p>
                        </div>
                    </div>
                    <button className="md-back-btn" onClick={() => navigate('/fleet')}>
                        ← Back to Fleet
                    </button>
                </div>

                {/* ── Step Indicator ── */}
                <div className="md-steps-bar">
                    {['Upload File', 'Preview & Validate', 'Complete'].map((label, i) => (
                        <React.Fragment key={i}>
                            <div className={`md-step ${stepIndex >= i ? 'active' : ''} ${stepIndex > i ? 'done' : ''}`}>
                                <div className="md-step-dot">
                                    {stepIndex > i
                                        ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20,6 9,17 4,12" /></svg>
                                        : i + 1
                                    }
                                </div>
                                <span>{label}</span>
                            </div>
                            {i < 2 && <div className={`md-step-line ${stepIndex > i ? 'done' : ''}`} />}
                        </React.Fragment>
                    ))}
                </div>

                {/* ── Content Card ── */}
                <div className="md-card">

                    {/* ══════════ STEP 1: UPLOAD ══════════ */}
                    {step === 'upload' && (
                        <div className="md-section">

                            {/* Info banner */}
                            <div className="md-info-banner">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="12" y1="8" x2="12" y2="12" />
                                    <line x1="12" y1="16" x2="12.01" y2="16" />
                                </svg>
                                <div>
                                    <strong>Use our Excel template for best results</strong>
                                    <p>Download the template — it has separate sheets for Vehicles and Tankers with sample data.</p>
                                </div>
                                <button className="md-template-btn" onClick={downloadTemplate}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7,10 12,15 17,10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    Download Template
                                </button>
                            </div>

                            {/* Drop zone */}
                            <div
                                className={`md-dropzone ${dragOver ? 'dragover' : ''}`}
                                onClick={() => fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                onDragLeave={() => setDragOver(false)}
                                onDrop={handleDrop}
                            >
                                <div className="md-drop-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                        <path d="M3 9h18M9 21V9" />
                                    </svg>
                                </div>
                                <p className="md-drop-title">
                                    {dragOver ? '📂 Drop file here!' : 'Click or drag & drop your Excel file'}
                                </p>
                                <p className="md-drop-sub">Supports .xlsx and .xls — max one file at a time</p>
                                <div className="md-format-pill">.xlsx &nbsp;/&nbsp; .xls</div>
                            </div>

                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />

                            {/* Column format reference */}
                            <div className="md-format-grid">
                                <div className="md-format-card vehicle-card">
                                    <div className="md-format-head">
                                        <span className="md-type-badge vehicle">Vehicle</span>
                                        <span>Required columns</span>
                                    </div>
                                    <div className="md-chips">
                                        {['type', 'truckNumber', 'model', 'location', 'currentReading', 'driverName', 'ownership', 'status'].map(f => (
                                            <span key={f} className="md-chip">{f}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="md-format-card tanker-card">
                                    <div className="md-format-head">
                                        <span className="md-type-badge tanker">Tanker</span>
                                        <span>Required columns</span>
                                    </div>
                                    <div className="md-chips">
                                        {['type', 'truckNumber', 'model', 'location', 'capacity', 'remainingOil', 'driverName', 'ownership', 'status'].map(f => (
                                            <span key={f} className="md-chip">{f}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ══════════ STEP 2: PREVIEW ══════════ */}
                    {step === 'preview' && (
                        <div className="md-section">

                            {/* Summary stat cards */}
                            <div className="md-stats-row">
                                <div className="md-stat-card total">
                                    <span className="md-stat-num">{rows.length}</span>
                                    <span className="md-stat-label">Total Rows</span>
                                </div>
                                <div className="md-stat-card valid">
                                    <span className="md-stat-num">{validRows.length}</span>
                                    <span className="md-stat-label">Ready to Upload</span>
                                </div>
                                <div className="md-stat-card vehicle">
                                    <span className="md-stat-num">{vehicleRows.length}</span>
                                    <span className="md-stat-label">Vehicles</span>
                                </div>
                                <div className="md-stat-card tanker">
                                    <span className="md-stat-num">{tankerRows.length}</span>
                                    <span className="md-stat-label">Tankers</span>
                                </div>
                                <div className="md-stat-card error">
                                    <span className="md-stat-num">{errorRows.length + dupRows.length}</span>
                                    <span className="md-stat-label">Issues</span>
                                </div>
                            </div>

                            {/* Toolbar */}
                            <div className="md-toolbar">
                                <div className="md-file-pill">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                        <polyline points="14,2 14,8 20,8" />
                                    </svg>
                                    <span>{fileName}</span>
                                    <span className="md-count-badge">{rows.length} rows</span>
                                </div>
                                <div className="md-filters">
                                    <select className="md-sel" value={filterType} onChange={e => setFilterType(e.target.value)}>
                                        <option value="All">All Types</option>
                                        <option value="Vehicle">Vehicle</option>
                                        <option value="Tanker">Tanker</option>
                                    </select>
                                    <select className="md-sel" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                                        <option value="All">All Status</option>
                                        <option value="valid">Valid Only</option>
                                        <option value="error">Issues Only</option>
                                    </select>
                                </div>
                            </div>

                            {/* Table */}
                            <div className="md-table-wrap">
                                <table className="md-table">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Type</th>
                                            <th>Number</th>
                                            <th>Model</th>
                                            <th>Location</th>
                                            <th>Driver</th>
                                            <th>Ownership</th>
                                            <th>Status</th>
                                            <th>Validation</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRows.map((row, i) => {
                                            const isValid = row._errors.length === 0 && !row._dup;
                                            return (
                                                <tr key={row._id} className={isValid ? '' : 'row-issue'}>
                                                    <td className="td-num">{i + 1}</td>
                                                    <td>
                                                        <span className={`md-type-badge ${String(row.type || '').toLowerCase()}`}>
                                                            {row.type || '—'}
                                                        </span>
                                                    </td>
                                                    <td className="td-mono">{row.truckNumber || '—'}</td>
                                                    <td>{row.model || '—'}</td>
                                                    <td>{row.location || '—'}</td>
                                                    <td>{row.driverName || '—'}</td>
                                                    <td>{row.ownership || '—'}</td>
                                                    <td>{row.status || '—'}</td>
                                                    <td>
                                                        {row._dup
                                                            ? <span className="md-tag dup">⚠ Duplicate</span>
                                                            : row._errors.length > 0
                                                                ? <span className="md-tag error" title={row._errors.join('\n')}>
                                                                    ✕ {row._errors.length} error{row._errors.length > 1 ? 's' : ''}
                                                                </span>
                                                                : <span className="md-tag valid">✓ Valid</span>
                                                        }
                                                    </td>
                                                    <td>
                                                        <button className="md-remove-btn" onClick={() => removeRow(row._id)} title="Remove this row">×</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {filteredRows.length === 0 && (
                                    <div className="md-empty">No rows match the current filter.</div>
                                )}
                            </div>

                            {/* Warning if issues exist */}
                            {(errorRows.length > 0 || dupRows.length > 0) && (
                                <div className="md-warn-box">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                        <line x1="12" y1="9" x2="12" y2="13" />
                                        <line x1="12" y1="17" x2="12.01" y2="17" />
                                    </svg>
                                    <div>
                                        <strong>{errorRows.length + dupRows.length} rows will be skipped.</strong>
                                        <p>Fix errors in your Excel and re-upload, or remove the rows above manually. Valid rows will still be uploaded.</p>
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            <div className="md-actions">
                                <button className="md-btn secondary" onClick={resetState}>
                                    ← Re-upload File
                                </button>
                                <button
                                    className="md-btn primary"
                                    disabled={validRows.length === 0}
                                    onClick={handleUpload}
                                >
                                    Upload {validRows.length} Record{validRows.length !== 1 ? 's' : ''}
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                        <line x1="12" y1="19" x2="12" y2="5" />
                                        <polyline points="5,12 12,5 19,12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ══════════ STEP 3: UPLOADING ══════════ */}
                    {step === 'uploading' && (
                        <div className="md-section md-uploading">
                            <div className="md-spinner-wrap">
                                <div className="md-spinner-ring" />
                                <div className="md-spinner-icon">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                        <path d="M3 17v3a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-3" />
                                        <polyline points="16,6 12,2 8,6" />
                                        <line x1="12" y1="2" x2="12" y2="15" />
                                    </svg>
                                </div>
                            </div>
                            <h3>Uploading to Firebase...</h3>
                            <p>Please keep this page open until complete.</p>
                            <div className="md-progress-wrap">
                                <div className="md-progress-bar" style={{ width: `${uploadProgress}%` }} />
                            </div>
                            <span className="md-progress-pct">{uploadProgress}%</span>
                        </div>
                    )}

                    {/* ══════════ STEP 4: DONE ══════════ */}
                    {step === 'done' && (
                        <div className="md-section md-done">
                            <div className="md-done-circle">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <polyline points="20,6 9,17 4,12" />
                                </svg>
                            </div>
                            <h3 className="md-done-title">Upload Complete!</h3>

                            <div className="md-done-stats">
                                <div className="md-done-stat success">
                                    <span className="md-done-num">{results.success}</span>
                                    <span>Successfully Added</span>
                                </div>
                                <div className="md-done-stat skipped">
                                    <span className="md-done-num">{dupRows.length + errorRows.length}</span>
                                    <span>Skipped</span>
                                </div>
                                {results.failed > 0 && (
                                    <div className="md-done-stat failed">
                                        <span className="md-done-num">{results.failed}</span>
                                        <span>Write Errors</span>
                                    </div>
                                )}
                            </div>

                            {results.errors.length > 0 && (
                                <div className="md-fail-list">
                                    <strong>Failed records:</strong>
                                    {results.errors.map((e, i) => (
                                        <div key={i} className="md-fail-row">
                                            <span className="td-mono">{e.truckNumber}</span>
                                            <span>{e.error}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <p className="md-done-note">
                                Dashboard and fleet list have been updated with the new records.
                            </p>

                            <div className="md-actions">
                                <button className="md-btn secondary" onClick={resetState}>
                                    Upload More Records
                                </button>
                                <button className="md-btn primary" onClick={() => navigate('/admin-dashboard')}>
                                    Go to Dashboard →
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}